import { TextField, type TextFieldProps, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import {
	type FieldValues,
	type Path,
	type UseControllerProps,
	useController,
} from "react-hook-form";

const StyledTextField = styled(TextField, {
	shouldForwardProp: (prop) => prop !== "isDimmed",
})<{ isDimmed: boolean }>(({ theme, isDimmed }) => ({
	"& .MuiInput-underline:before": {
		borderBottom: isDimmed
			? `1px solid ${theme.palette.grey[500]}`
			: `1px solid ${theme.palette.grey[300]}`,
	},

	"& .MuiInput-underline:hover:not(.Mui-disabled):before": {
		borderBottom: `2px solid ${theme.palette.primary.main}`,
	},

	"& .MuiInput-underline:after": {
		borderBottom: `2px solid ${theme.palette.primary.main}`,
	},

	"& .MuiInputLabel-root": {
		transition: theme.transitions.create(["transform", "color"], {
			duration: theme.transitions.duration.shorter,
		}),
	},
}));

export type FormInputTextProps<
	T extends FieldValues,
	P extends Path<T>,
> = TextFieldProps &
	UseControllerProps<T, P> & { isDimmed?: boolean; text?: string };

export default function FormInputText<
	T extends FieldValues,
	P extends Path<T>,
>({
	name,
	control,
	defaultValue,
	rules,
	isDimmed = false,
	variant = "outlined",
	text,
	...props
}: FormInputTextProps<T, P>) {
	const {
		field: { ref, onChange: formOnChange, value: formValue, ...field },
		fieldState: { error },
	} = useController({ name, control, rules, defaultValue });

	// Handle number type inputs to parse the value correctly
	let onChange = formOnChange;
	if (props.type === "number") {
		onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const parsedValue = Number.parseFloat(e.target.value);
			formOnChange(Number.isNaN(parsedValue) ? "" : parsedValue);
		};
	}

	return (
		<>
			{text && (
				<Typography
					variant="body2"
					component="label"
					htmlFor={name}
					sx={{ mb: "6px" }}
				>
					{text}
				</Typography>
			)}
			<StyledTextField
				{...field}
				variant={variant}
				value={formValue ?? ""}
				inputRef={ref}
				onChange={onChange}
				helperText={error ? error.message : null}
				error={!!error}
				required={!!rules?.required}
				isDimmed={isDimmed}
				{...props}
			/>
		</>
	);
}
